'use client';

import React from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { discardHTMLTags, getCurrencySymbol, getFinalDiscountTag, getMerchantHref, getProductDetailHref } from '@/constants/hooks';
import OfferOutUrl from '@/components/shared/OfferOutUrl';
import { BrandedProduct, BrandedProductMerchant } from '@/services/dataTypes';
import OfferModalTrigger from './OfferModalTrigger';

interface Props {
  product: BrandedProduct;
  domain: string;
  imageSrc: string;
  merchant: BrandedProductMerchant;
  mer_slug?: string;
  mer_slug_type: string;
}

const BrandedProductCard = ({ product, domain, imageSrc, merchant, mer_slug = 'store', mer_slug_type }: Props) => {
  const type = product?.offer_type?.name;
  const originalPrice = product?.original_price ? parseFloat(product?.original_price) : 0;
  const salePrice = product?.sale_price ? parseFloat(product?.sale_price) : 0;
  const discountPercent = originalPrice > 0 && salePrice > 0
    ? Math.round(((originalPrice - salePrice) / originalPrice) * 100)
    : null;

  const finalDiscountTag = getFinalDiscountTag(
    product?.offer_title,
    discountPercent,
  );

  const merchantName = merchant?.merchant_name || 'Brand';
  const merchantHref = merchant ? getMerchantHref(merchant, mer_slug, mer_slug_type) : '/products';
  const productDetailUrl = product?.slug
    ? (merchant ? getProductDetailHref(merchant, mer_slug_type, product.slug, product?.category?.slug) : `/products/${product.slug}`)
    : null;

  return (
    <div className="group relative bg-white rounded-3xl border border-slate-100 shadow-[0_4px_25px_rgba(0,0,0,0.03)] hover:shadow-[0_20px_40px_rgba(79,70,229,0.12)] transition-all duration-300 flex flex-col h-full overflow-hidden hover:-translate-y-1">
      {/* Badge */}
      {finalDiscountTag && (
        <div className="absolute top-2 right-2 z-20 inline-flex items-center px-2 py-0.5 rounded-md text-[10px] sm:text-[11px] font-black bg-indigo-50 text-indigo-600 border border-indigo-100 shrink-0 max-w-[80%] truncate">
          {finalDiscountTag}
        </div>
      )}

      {/* Image Area */}
      <div className="relative w-full h-36 sm:h-40 p-4 flex items-center justify-center overflow-hidden bg-slate-50/50">
        <Image
          src={imageSrc}
          alt={discardHTMLTags(product?.offer_title) || 'Product'}
          className="object-contain max-h-[110px] w-auto h-auto group-hover:scale-105 transition-transform duration-500"
          height={140}
          width={140}
        />
      </div>

      {/* Card Content */}
      <div className="p-6 pt-2 flex flex-col flex-grow justify-between">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <span className="text-[11px] font-bold text-slate-600 uppercase tracking-wider">
              {merchant?.merchant_name}
            </span>
          </div>

          {/* Product Title */}
          {productDetailUrl ? (
            <Link href={productDetailUrl} className="block no-underline">
              <h3 className="text-sm font-bold text-center text-slate-800 hover:text-indigo-600 line-clamp-2 mb-2 group-hover:text-indigo-600 transition-colors">
                {discardHTMLTags(product?.offer_title)}
              </h3>
            </Link>
          ) : (
            <h3 className="text-sm font-bold text-center text-slate-800 hover:text-indigo-600 line-clamp-2 mb-2 group-hover:text-indigo-600 transition-colors">
              {discardHTMLTags(product?.offer_title)}
            </h3>
          )}

          {/* Price (if applicable) */}
          {(salePrice > 0 || originalPrice > 0) && (
            <div className="flex items-center justify-center gap-2 mb-2">
              {salePrice > 0 && (
                <span className="text-lg font-black text-indigo-600 tracking-tight leading-none">
                  {getCurrencySymbol(product?.currency)}{product?.sale_price}
                </span>
              )}
              {originalPrice > 0 && salePrice > 0 && (
                <span className="text-xs line-through text-slate-400 font-semibold mb-0.5">
                  {getCurrencySymbol(product?.currency)}{product?.original_price}
                </span>
              )}
            </div>
          )}
        </div>

        <div className="mt-3">
          {/* View Details on Right Side */}
          <div className="flex justify-end mb-2.5">
            <OfferModalTrigger
              product={product as any}
              merchantName={merchantName}
              merchantLogo={imageSrc}
              domain={domain}
              className="text-[11px] font-bold text-slate-900 hover:text-indigo-600 transition-colors tracking-wider cursor-pointer"
            >
              View Details
            </OfferModalTrigger>
          </div>

          <OfferOutUrl
            unique_id={product?.unique_id}
            outUrl={product?.url}
            merchantHref={merchantHref}
            domain={domain}
            customClass="w-full bg-slate-900 hover:bg-indigo-600 text-white font-bold text-xs py-3 rounded-xl transition-all duration-300 flex items-center justify-center tracking-wider shadow-md hover:shadow-indigo-500/20 active:scale-[0.98]"
          >
            <span>
              {product?.coupon_code ? 'Show Code' : (type === 'product' ? 'Buy Now' : 'Get Deal')}
            </span>
          </OfferOutUrl>
        </div>
      </div>
    </div>
  );
};

export default BrandedProductCard;
